<template>
  <div class="min-w-0 space-y-5">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <span class="panel-glass-chip inline-flex max-w-full items-center gap-1.5 overflow-hidden rounded-full px-3 py-1 text-xs font-semibold">
        <span class="relative flex size-2 shrink-0 overflow-hidden rounded-full">
          <span class="absolute inline-flex size-full animate-ping rounded-full bg-brand-400 opacity-60" />
          <span class="relative inline-flex size-2 rounded-full bg-brand-500" />
        </span>
        {{ __('Active Shipping') }}
      </span>

      <div class="flex items-center gap-3 text-sm text-zinc-500 dark:text-zinc-400">
        <HeroiconsOutlineClock class="size-4" />
        <span>{{ currentTime }}</span>
        <span class="text-zinc-300 dark:text-zinc-600">|</span>
        <span>{{ currentDate }}</span>
      </div>
    </div>

    <div class="grid min-w-0 gap-4 xl:grid-cols-12 xl:items-stretch">
      <!-- Tracking list -->
      <div class="space-y-4 xl:col-span-4">
        <article class="panel-glass flex max-h-[44rem] flex-col rounded-[2rem] p-4 md:p-5 xl:sticky xl:top-24">
          <div class="panel-glass-content flex min-h-0 flex-1 flex-col">
            <div class="mb-4 flex shrink-0 items-center justify-between gap-3">
              <h2 class="text-sm font-semibold text-zinc-900 dark:text-white">
                {{ __('Your Order Tracking List') }}
              </h2>
              <button
                type="button"
                class="panel-glass-btn flex size-9 items-center justify-center rounded-full"
                :aria-label="__('Filter')"
              >
                <HeroiconsOutlineFilter class="size-4" />
              </button>
            </div>

            <label class="relative mb-4 block shrink-0">
              <HeroiconsOutlineSearch class="pointer-events-none absolute start-3 top-1/2 size-4 -translate-y-1/2 text-zinc-400" />
              <input
                v-model="trackingSearch"
                type="search"
                class="panel-glass-input w-full rounded-2xl py-2.5 ps-10 pe-4 text-sm"
                :placeholder="__('Search shipment ID or route...')"
              >
            </label>

            <div class="panel-scrollbar min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pe-1">
              <button
                v-for="item in filteredTrackingList"
                :key="item.id"
                type="button"
                class="panel-glass-card w-full rounded-3xl p-4 text-start"
                :class="{ 'panel-glass-card--active': selectedShipmentId === item.id }"
                @click="handleTrackingSelect(item.id)"
              >
                <div class="panel-glass-content">
                  <div class="flex flex-wrap items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="truncate text-xs font-semibold text-zinc-900 dark:text-white">
                        {{ item.code }}
                      </p>
                      <p class="mt-0.5 text-[11px] text-zinc-500 dark:text-zinc-400">
                        {{ item.tag }}
                      </p>
                    </div>
                    <span
                      class="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold tracking-wide"
                      :class="statusTone(item.status)"
                    >
                      {{ __(item.status) }}
                    </span>
                  </div>

                  <div class="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <p class="text-[10px] font-bold text-zinc-400">
                        {{ item.originCode }}
                      </p>
                      <p class="mt-0.5 text-xs font-medium text-zinc-800 dark:text-zinc-200">
                        {{ item.origin.label }}
                      </p>
                      <p class="mt-1 text-[10px] text-zinc-500 dark:text-zinc-400">
                        {{ item.originSchedule }}
                      </p>
                    </div>
                    <div class="text-end">
                      <p class="text-[10px] font-bold text-zinc-400">
                        {{ item.destinationCode }}
                      </p>
                      <p class="mt-0.5 text-xs font-medium text-zinc-800 dark:text-zinc-200">
                        {{ item.destination.label }}
                      </p>
                      <p class="mt-1 text-[10px] text-zinc-500 dark:text-zinc-400">
                        {{ item.destinationSchedule }}
                      </p>
                    </div>
                  </div>

                  <div class="mt-4 flex items-center gap-2">
                    <HeroiconsOutlineCube class="size-3.5 shrink-0 text-zinc-400" />
                    <div class="relative h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-200/90 dark:bg-white/10">
                      <div
                        class="absolute inset-y-0 start-0 rounded-full bg-brand-500"
                        :style="{ width: `${item.progress}%` }"
                      />
                    </div>
                    <HeroiconsOutlineLocationMarker class="size-3.5 shrink-0 text-brand-500" />
                  </div>
                </div>
              </button>
            </div>
          </div>
        </article>
      </div>

      <!-- Map + operation details -->
      <div class="flex min-w-0 flex-col gap-4 xl:col-span-5 xl:h-full">
        <article class="panel-glass overflow-hidden rounded-[2rem]">
          <div class="panel-glass-content panel-glass-divider flex min-w-0 items-center justify-between gap-2 border-b px-4 py-3 md:gap-3 md:px-5">
            <h2 class="min-w-0 truncate text-sm font-semibold text-zinc-900 dark:text-white">
              {{ __('Live Tracking Map') }}
            </h2>
            <div class="flex shrink-0 items-center gap-1.5">
              <button
                type="button"
                class="panel-glass-btn flex size-8 items-center justify-center rounded-full"
                :aria-label="__('Zoom in')"
                @click="$refs.trackingMap?.zoomIn()"
              >
                <HeroiconsOutlinePlus class="size-3.5" />
              </button>
              <button
                type="button"
                class="panel-glass-btn flex size-8 items-center justify-center rounded-full"
                :aria-label="__('Zoom out')"
                @click="$refs.trackingMap?.zoomOut()"
              >
                <HeroiconsOutlineMinus class="size-3.5" />
              </button>
            </div>
          </div>

          <ShipmentTrackingMap
            v-if="selectedShipment"
            ref="trackingMap"
            :origin="selectedShipment.origin"
            :destination="selectedShipment.destination"
            :progress="selectedShipment.progress"
            :eta-label="selectedMapEta"
          />
        </article>

        <article class="panel-glass flex flex-col rounded-[2rem] p-4 md:p-5 xl:min-h-0 xl:flex-1">
          <div class="panel-glass-content flex min-h-0 flex-1 flex-col">
            <div class="mb-4 flex shrink-0 items-center justify-between gap-3">
              <h2 class="text-sm font-semibold text-zinc-900 dark:text-white">
                {{ __('Operation Details') }}
              </h2>
              <button
                type="button"
                class="panel-glass-btn flex size-8 items-center justify-center rounded-full"
                :aria-label="__('More options')"
              >
                <HeroiconsOutlineDotsVertical class="size-4" />
              </button>
            </div>

            <div class="flex min-h-0 min-w-0 flex-1 gap-3 sm:gap-4">
              <div class="flex shrink-0 flex-col gap-3.5 pt-0.5 md:gap-2">
                <button
                  v-for="(icon, index) in operationIcons"
                  :key="icon.key"
                  type="button"
                  class="panel-glass-btn flex size-9 items-center justify-center rounded-2xl"
                  :class="{ 'panel-glass-btn--active': index === 0 }"
                  :aria-label="icon.label"
                >
                  <component :is="icon.component" class="size-4" />
                </button>
              </div>

              <div class="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                <div class="flex items-start gap-3">
                  <div class="panel-glass-card flex size-20 shrink-0 items-center justify-center rounded-3xl sm:size-28">
                    <div class="panel-glass-content">
                      <HeroiconsOutlineTruck class="size-10 text-zinc-400 sm:size-12" />
                    </div>
                  </div>

                  <div class="min-w-0 flex-1 pt-1">
                    <p class="truncate text-sm font-semibold leading-snug text-zinc-900 dark:text-white">
                      {{ selectedShipment?.code }}
                    </p>
                    <p class="mt-1 truncate text-xs text-zinc-500 dark:text-zinc-400">
                      {{ selectedShipment?.tag }}
                    </p>

                    <div class="mt-2 hidden grid-cols-2 gap-2 md:grid">
                      <div class="panel-glass-card min-w-0 rounded-2xl px-3 py-2.5">
                        <div class="panel-glass-content">
                          <p class="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                            {{ __('Container Type') }}
                          </p>
                          <p class="mt-1 truncate text-xs font-bold text-zinc-900 dark:text-white">
                            FCL 20 Standard
                          </p>
                        </div>
                      </div>
                      <div class="panel-glass-card min-w-0 rounded-2xl px-3 py-2.5">
                        <div class="panel-glass-content">
                          <p class="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                            {{ __('Available Cargo') }}
                          </p>
                          <p class="mt-1 truncate text-xs font-bold text-zinc-900 dark:text-white">
                            6,791kg / 8,000kg
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="mt-2 grid grid-cols-2 gap-2 md:hidden">
                  <div class="panel-glass-card min-w-0 rounded-2xl px-3 py-2.5">
                    <div class="panel-glass-content">
                      <p class="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                        {{ __('Container Type') }}
                      </p>
                      <p class="mt-1 truncate text-xs font-bold text-zinc-900 dark:text-white">
                        FCL 20 Standard
                      </p>
                    </div>
                  </div>
                  <div class="panel-glass-card min-w-0 rounded-2xl px-3 py-2.5">
                    <div class="panel-glass-content">
                      <p class="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                        {{ __('Available Cargo') }}
                      </p>
                      <p class="mt-1 truncate text-xs font-bold text-zinc-900 dark:text-white">
                        6,791kg / 8,000kg
                      </p>
                    </div>
                  </div>
                </div>

                <div class="panel-glass-card mt-auto shrink-0 rounded-3xl px-3 py-3 md:my-auto">
                  <div class="panel-glass-content flex min-w-0 items-center justify-between gap-3">
                    <div class="flex min-w-0 items-center gap-3">
                      <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-sm font-bold text-brand-600 dark:text-brand-400">
                        {{ selectedDriver.initials }}
                      </span>
                      <div class="min-w-0">
                        <p class="truncate text-sm font-semibold text-zinc-900 dark:text-white">
                          {{ selectedDriver.name }}
                        </p>
                        <p class="truncate text-xs text-zinc-500 dark:text-zinc-400">
                          {{ selectedDriver.phone }} · {{ __('online') }}
                        </p>
                      </div>
                    </div>
                    <div class="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        class="panel-glass-btn flex size-9 items-center justify-center rounded-full"
                        :aria-label="__('Call driver')"
                      >
                        <HeroiconsOutlinePhone class="size-4" />
                      </button>
                      <button
                        type="button"
                        class="panel-glass-btn flex size-9 items-center justify-center rounded-full"
                        :aria-label="__('Message driver')"
                      >
                        <HeroiconsOutlineChat class="size-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>

      <!-- Weight distribution -->
      <div class="flex min-w-0 flex-col xl:col-span-3">
        <article class="panel-glass flex h-full min-h-[28rem] flex-col rounded-[2rem] p-4 md:min-h-0 md:p-5">
          <div class="panel-glass-content flex min-h-0 flex-1 flex-col">
            <div class="mb-4 flex shrink-0 items-center justify-between gap-3">
              <h2 class="text-sm font-semibold text-zinc-900 dark:text-white">
                {{ __('Weight Distribution') }}
              </h2>
              <button
                type="button"
                class="panel-glass-btn flex size-8 items-center justify-center rounded-full"
                :aria-label="__('More options')"
              >
                <HeroiconsOutlineDotsVertical class="size-4" />
              </button>
            </div>

            <p class="text-3xl font-bold text-zinc-900 dark:text-white">
              {{ selectedCapacityPercent }}%
            </p>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
              {{ __('of Total Capacity') }}
            </p>

            <div class="mt-5 overflow-hidden">
              <div class="relative h-2 overflow-hidden rounded-full bg-zinc-200/90 dark:bg-white/10">
                <div
                  class="absolute inset-y-0 start-0 rounded-full bg-brand-500"
                  :style="{ width: `${selectedCapacityPercent}%` }"
                />
                <div
                  class="absolute top-1/2 z-10 size-4 -translate-y-1/2 rounded-full border-2 border-white bg-brand-500"
                  :style="{ left: `calc(${selectedCapacityPercent}% - 8px)` }"
                />
              </div>
              <p class="mt-2 text-end text-xs font-semibold text-zinc-700 dark:text-zinc-300">
                {{ selectedCapacityWeight }}
              </p>
            </div>

            <div class="mt-5 flex min-h-16 flex-1 items-end gap-1">
              <div
                v-for="(height, index) in capacityBars"
                :key="index"
                class="flex-1 rounded-t-md bg-brand-500/20 dark:bg-brand-500/25"
                :style="{ height: `${height}%` }"
              />
            </div>

            <div class="panel-glass-divider mt-5 shrink-0 space-y-2 border-t pt-4">
              <div
                v-for="row in cargoBreakdown"
                :key="row.label"
                class="panel-glass-card flex items-center justify-between gap-3 rounded-2xl px-3 py-2.5"
              >
                <div class="panel-glass-content flex min-w-0 flex-1 items-center justify-between gap-3">
                  <div class="flex min-w-0 items-center gap-2.5">
                    <span class="panel-glass-btn flex size-8 shrink-0 items-center justify-center rounded-xl">
                      <component :is="row.component" class="size-3.5" />
                    </span>
                    <span class="truncate text-xs font-medium text-zinc-800 dark:text-zinc-200">
                      {{ __(row.label) }}
                    </span>
                  </div>
                  <span class="shrink-0 text-[11px] font-semibold text-zinc-500 dark:text-zinc-400">
                    {{ row.weight }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import HeroiconsOutlineCalendar from '@/components/Heroicons/outline/HeroiconsOutlineCalendar'
import HeroiconsOutlineChat from '@/components/Heroicons/outline/HeroiconsOutlineChat'
import HeroiconsOutlineClock from '@/components/Heroicons/outline/HeroiconsOutlineClock'
import HeroiconsOutlineCube from '@/components/Heroicons/outline/HeroiconsOutlineCube'
import HeroiconsOutlineCurrencyDollar from '@/components/Heroicons/outline/HeroiconsOutlineCurrencyDollar'
import HeroiconsOutlineHeart from '@/components/Heroicons/outline/HeroiconsOutlineHeart'
import HeroiconsOutlineDotsVertical from '@/components/Heroicons/outline/HeroiconsOutlineDotsVertical'
import HeroiconsOutlineFilter from '@/components/Heroicons/outline/HeroiconsOutlineFilter'
import HeroiconsOutlineLightningBolt from '@/components/Heroicons/outline/HeroiconsOutlineLightningBolt'
import HeroiconsOutlineLocationMarker from '@/components/Heroicons/outline/HeroiconsOutlineLocationMarker'
import HeroiconsOutlineMinus from '@/components/Heroicons/outline/HeroiconsOutlineMinus'
import HeroiconsOutlinePhone from '@/components/Heroicons/outline/HeroiconsOutlinePhone'
import HeroiconsOutlinePlus from '@/components/Heroicons/outline/HeroiconsOutlinePlus'
import HeroiconsOutlineSearch from '@/components/Heroicons/outline/HeroiconsOutlineSearch'
import HeroiconsOutlineShoppingCart from '@/components/Heroicons/outline/HeroiconsOutlineShoppingCart'
import HeroiconsOutlineTruck from '@/components/Heroicons/outline/HeroiconsOutlineTruck'
import ShipmentTrackingMap from '@/components/Cards/ShipmentTrackingMap.vue'

const shipmentStatuses = {
  inTransit: 'IN TRANSIT',
  pending: 'PENDING',
  delivered: 'DELIVERED',
}

const trackingList = [
  {
    id: 'SH-2025-001',
    code: '2025 - FASLOG - C4N5A45',
    tag: '#JAKQHH671',
    status: shipmentStatuses.inTransit,
    originCode: 'WDH',
    origin: { lat: -22.5609, lng: 17.0658, label: 'Windhoek, Namibia' },
    originSchedule: 'Mon, 10.07 - 08:30',
    destinationCode: 'JFK',
    destination: { lat: 40.6413, lng: -73.7781, label: 'New York, USA' },
    destinationSchedule: 'Sun, 10.13 - 14:00',
    progress: 62,
    capacityPercent: 80,
    capacityWeight: '6,791kg',
    driver: {
      initials: 'CK',
      name: 'Christina Kamberina',
      phone: '+1 202 555 0142',
    },
  },
  {
    id: 'SH-2025-002',
    code: '2025 - FASLOG - B8K2M11',
    tag: '#PLMRT892',
    status: shipmentStatuses.pending,
    originCode: 'IKA',
    origin: { lat: 35.6892, lng: 51.389, label: 'Tehran, Iran' },
    originSchedule: 'Wed, 10.09 - 06:00',
    destinationCode: 'DXB',
    destination: { lat: 25.2048, lng: 55.2708, label: 'Dubai, UAE' },
    destinationSchedule: 'Thu, 10.10 - 18:45',
    progress: 18,
    capacityPercent: 42,
    capacityWeight: '3,360kg',
    driver: {
      initials: 'AR',
      name: 'Ali Rezaei',
      phone: '+98 912 555 0142',
    },
  },
  {
    id: 'SH-2025-003',
    code: '2025 - FASLOG - D3P7Q88',
    tag: '#VNQWE441',
    status: shipmentStatuses.inTransit,
    originCode: 'IST',
    origin: { lat: 41.0082, lng: 28.9784, label: 'Istanbul, Turkey' },
    originSchedule: 'Fri, 10.04 - 11:15',
    destinationCode: 'FRA',
    destination: { lat: 50.1109, lng: 8.6821, label: 'Frankfurt, Germany' },
    destinationSchedule: 'Sat, 10.05 - 09:20',
    progress: 84,
    capacityPercent: 91,
    capacityWeight: '7,280kg',
    driver: {
      initials: 'MK',
      name: 'Mehmet Kaya',
      phone: '+90 532 555 0142',
    },
  },
  {
    id: 'SH-2025-004',
    code: '2025 - FASLOG - H1M9K33',
    tag: '#SHGTR118',
    status: shipmentStatuses.inTransit,
    originCode: 'PVG',
    origin: { lat: 31.1443, lng: 121.8083, label: 'Shanghai, China' },
    originSchedule: 'Tue, 10.08 - 10:00',
    destinationCode: 'RTM',
    destination: { lat: 51.9244, lng: 4.4777, label: 'Rotterdam, Netherlands' },
    destinationSchedule: 'Mon, 10.20 - 16:30',
    progress: 41,
    capacityPercent: 55,
    capacityWeight: '4,400kg',
    driver: {
      initials: 'LW',
      name: 'Li Wei',
      phone: '+86 138 555 0142',
    },
  },
]

export default {
  name: 'DriverManagementDashboard',

  props: {
    card: {
      type: Object,
      required: true,
    },
  },

  components: {
    ShipmentTrackingMap,
    HeroiconsOutlineCalendar,
    HeroiconsOutlineChat,
    HeroiconsOutlineClock,
    HeroiconsOutlineCube,
    HeroiconsOutlineCurrencyDollar,
    HeroiconsOutlineHeart,
    HeroiconsOutlineDotsVertical,
    HeroiconsOutlineFilter,
    HeroiconsOutlineLightningBolt,
    HeroiconsOutlineLocationMarker,
    HeroiconsOutlineMinus,
    HeroiconsOutlinePhone,
    HeroiconsOutlinePlus,
    HeroiconsOutlineSearch,
    HeroiconsOutlineShoppingCart,
    HeroiconsOutlineTruck,
  },

  data: () => ({
    trackingSearch: '',
    selectedShipmentId: 'SH-2025-001',
    trackingList,
    capacityBars: [28, 42, 36, 58, 48, 72, 64, 80, 68, 76],
    cargoBreakdown: [
      {
        label: 'Electronic Product',
        weight: '100kg Total',
        component: 'HeroiconsOutlineLightningBolt',
      },
      {
        label: 'Food And Beverage',
        weight: '420kg Total',
        component: 'HeroiconsOutlineShoppingCart',
      },
      {
        label: 'Industrial Materials',
        weight: '1.2t Total',
        component: 'HeroiconsOutlineCube',
      },
      {
        label: 'Medical Equipment',
        weight: '280kg Total',
        component: 'HeroiconsOutlineHeart',
      },
    ],
  }),

  computed: {
    operationIcons() {
      return [
        { key: 'truck', label: this.__('Truck'), component: 'HeroiconsOutlineTruck' },
        { key: 'map', label: this.__('Map'), component: 'HeroiconsOutlineLocationMarker' },
        { key: 'wallet', label: this.__('Wallet'), component: 'HeroiconsOutlineCurrencyDollar' },
        { key: 'calendar', label: this.__('Calendar'), component: 'HeroiconsOutlineCalendar' },
        { key: 'clock', label: this.__('Clock'), component: 'HeroiconsOutlineClock' },
      ]
    },

    filteredTrackingList() {
      const query = this.trackingSearch.trim().toLowerCase()

      if (!query) {
        return this.trackingList
      }

      return this.trackingList.filter(item =>
        item.code.toLowerCase().includes(query)
        || item.tag.toLowerCase().includes(query)
        || item.origin.label.toLowerCase().includes(query)
        || item.destination.label.toLowerCase().includes(query)
      )
    },

    selectedShipment() {
      return (
        this.trackingList.find(item => item.id === this.selectedShipmentId)
        ?? this.trackingList[0]
      )
    },

    selectedDriver() {
      return this.selectedShipment?.driver ?? {
        initials: 'CK',
        name: 'Christina Kamberina',
        phone: '+1 202 555 0142',
      }
    },

    selectedCapacityPercent() {
      return this.selectedShipment?.capacityPercent ?? 80
    },

    selectedCapacityWeight() {
      return this.selectedShipment?.capacityWeight ?? '6,791kg'
    },

    selectedMapEta() {
      const schedule = this.selectedShipment?.destinationSchedule ?? ''
      const eta = schedule.includes(' - ') ? schedule.split(' - ')[1] : schedule

      return `${this.__('Drop-off ETA')} · ${eta}`
    },

    currentTime() {
      const now = new Date()
      return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    currentDate() {
      return new Date().toLocaleDateString([], {
        month: 'long',
        day: 'numeric',
      })
    },
  },

  methods: {
    handleTrackingSelect(id) {
      this.selectedShipmentId = id
    },

    statusTone(status) {
      if (status === shipmentStatuses.inTransit) {
        return 'bg-amber-500/10 text-amber-600 dark:text-amber-400'
      }

      if (status === shipmentStatuses.pending) {
        return 'bg-brand-500/10 text-brand-600 dark:text-brand-400'
      }

      return 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
    },
  },
}
</script>
